import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "Lambda pipelines for serverless data processing",
  "description": "Build a robust massively distributed data, event, or message processing pipeline",
  "image": "./img/lambda-workflows.png"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <h1 {...{
      "id": "lambda-pipelines-for-serverless-data-processing"
    }}>{`Lambda pipelines for serverless data processing`}</h1>
    <p><img parentName="p" {...{
        "src": "/62fa4d6e1c983f0a36cb2b45101e58c5/lambda-workflows.svg",
        "alt": null
      }}></img></p>
    <p>{`You get tens of thousands of events per hour. How do you process that?`}</p>
    <p>{`You've got a ton of data. What do you do?`}</p>
    <p>{`Users send hundreds of messages per minute. Now what?`}</p>
    <p>{`You could learn Elixir and Erlang – purpose built languages for message processing used in networking. But is that where you want your career to go?`}</p>
    <p>{`You could try `}<a parentName="p" {...{
        "href": "https://kafka.apache.org/"
      }}>{`Kafka`}</a>{` or `}<a parentName="p" {...{
        "href": "https://hadoop.apache.org/"
      }}>{`Hadoop`}</a>{`. Tools designed for big data, used by large organizations for mind-boggling amounts of data. Are you ready for that?`}</p>
    <p>{`Elixir, Erlang, Kafka, Hadoop are wonderful tools, if you know how to use them. But there's a significant learning curve and devops work to keep them running.`}</p>
    <p>{`You have to maintain servers, write code in obscure languages, and deal with problems we're trying to avoid.`}</p>
    <h2 {...{
      "id": "serverless-data-processing"
    }}>{`Serverless data processing`}</h2>
    <p>{`Instead, you can leverage existing skills to build a data processing pipeline.`}</p>
    <p>{`I've used this approach to process millions of daily events with barely a 0.0007% loss of data. A rate of 7 events lost per 1,000,000.`}<sup parentName="p" {...{
        "id": "fnref-1"
      }}><a parentName="sup" {...{
          "href": "#fn-1",
          "className": "footnote-ref"
        }}>{`1`}</a></sup></p>
    <p>{`We used it to gather business and engineering analytics. A distributed `}<inlineCode parentName="p">{`console.log`}</inlineCode>{` that writes to a central database. That's how I know you should never build a distributed logging system unless it's your core business 😉`}</p>
    <p><figure parentName="p" {...{
        "className": "gatsby-resp-image-figure",
        "style": {
          "textAlign": "center",
          "fontStyle": "italic"
        }
      }}>{`
    `}<span parentName="figure" {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "890px"
          }
        }}>{`
      `}<a parentName="span" {...{
            "className": "gatsby-resp-image-link",
            "href": "/static/118c6e0ab8f4b20d6cce3ebb97e94581/6568d/distributed-logging.png",
            "style": {
              "display": "block"
            },
            "target": "_blank",
            "rel": "noopener"
          }}>{`
    `}<span parentName="a" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "87.44394618834082%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'349\\'%20viewBox=\\'0%200%20400%20349\\'%20preserveAspectRatio=\\'none\\'%3e%3cpath%20d=\\'M38%205l-4%202c-1%201-1%201%201%201%203-2%2012-2%2021-1%207%200%208%200%205%201l-4%204c-1%204%200%205%207%204%205-2%206-2%206-6%200-1%200-2%203-1%203%200%203%200%203%205l-2%2011-3%2022%203-11%202-10%202-13c0-6%202-5-17-8H38m-6%2015l-2%2021v9l7-1%2018%201c11%201%2014%200%2014-1l-5-1H31v-6a240%20240%200%20002-33l-1%2011m54%203c0%202%206%206%2029%2019a398%20398%200%200171%2050c5%203%207%203%208-2v-5c1-3-1-2-1%201-1%203-1%203-5%202l-8-6-12-7-13-9-11-9-27-16a547%20547%200%2001-31-18M24%2066c-3%201-1%202%204%201l15-1c9%201%209%201%207%203-3%201-5%205-4%207%201%201%2010%200%2012-1l1-4c0-2%201-2%203-2%206%200%201-2-9-3H24m-3%208a320%20320%200%2001-2%2034%20215%20215%200%200038%202c3-1-1-2-18-2s-18%200-18-2a1022%201022%200%20000-32m55%2015c2%202%2030%209%2047%2010a353%20353%200%200161%2016c1-1-11-7-18-8-13-4-40-9-51-10a272%20272%200%2001-39-8m140%208c-3%202-4%204-2%204l3-1c2-3%2011-2%2013%200s4%2010%205%2017l1%205-6%204c-13%209-26%2020-25%2021l3-1c4-4%2028-20%2029-20l3%2023%204%208c4%204%206%202%202-3l-2-8-10-46c-3-5-13-7-18-3m151-1l-8%204-1%202c-2%201-3%203-3%206a285%20285%200%2001-5%2041c0%208%2033%207%2037-1l1-3c-2%201%200-12%203-25%205-16%205-19%201-21l-11-2a8417%208417%200%2001-14-1m-1%202c-2%201-3%201-2%202%202%200%203%202%201%202-2-1-7%201-7%202%202%202%2028%203%2033%201l3-1-2-2c-1-2-4-2-12-4h-14m-9%2013c-2%2013-2%2012%2010%2014%2017%203%2021%201%2024-9%203-9%203-10%200-9h-32c-2-1-2-1-2%204m-331%207c-3%201-3%202%2010%201l16%201c4%201%204%201%201%201s-3%201-4%203c-2%205-2%205%204%204%206%200%208-1%208-5%200-2%201-3%204-1%203%200%202%208%200%2018-2%208-4%2019-3%2020l2-7%203-14c3-12%203-18%200-18-10-3-36-5-41-3m289%200c0%201%201%202%207%202l6%202-21%201a622%20622%200%200027%201c2%202%202%202%201%203-2%202-12%205-14%205s-3%201-3%202c1%202%2020-5%2020-8%200-4-20-12-23-8M23%20133l-2%2021v9h19l20-1-34-2-4%201%201-6a213%20213%200%20001-34l-1%2012m332-6c-2%2010-2%2011%201%2012%205%202%2026%202%2028%200l3-6%201-6-5%201-26-3c-2-1-2%200-2%202m-178%206c-3%202-2%202%202%200%204-1%206%200%204%202l-25%202a556%20556%200%200019%202l-1%203%205-4%205-6c-2-2-8-1-9%201m175%2012c0%206%200%206%203%207%205%202%2020%201%2026-1%204-1%205-2%205-6s-1-4-2-4c-5%202-19%202-23%201-10-3-9-3-9%203m-161-1a479%20479%200%2000-65%2027%20275%20275%200%2001-63%2026c0%202%205%201%2012-1l53-24%2054-22c12-3%2012-3%2011%200v3l3-4c3-4%203-6%200-7l-5%202m-4%2019l-4%204%206-3%203-2-5%205-19%2013a1558%201558%200%200119-11c3-2%203-2%203%200a75014%2075014%200%20015-8c1-2-4-1-8%202m15%203c-6%202-8%204-8%206s1%201%203-1l3-2-6%208-23%2026a1358%201358%200%20012%200%20846%20846%200%200133-34l-1%206v4l1-5%202-6c1-2%200-3-1-3l-5%201m-169%209l-8%202-5%2031-2%2012c0%206%201%206%2017%205%2014%200%2015-1%2015-7l3-14c2-10%203-19%201-23-2-6-8-7-21-6m-7%203l-2%2011a921%20921%200%2001-4%2032v3h10c18%200%2018-1%2018-5l3-15c5-20%203-29-6-26-5%202-12%202-14%200h-5m14%2058l-7%202c-1%201-4%2015-4%2021l-1%2010-1%2011-1%206h4c5%201%2026%200%2027-2l1-6%203-14c4-15%203-24-4-27-4-2-6-2-17-1m-6%203l-4%2021-1%2012-1%209v4l13-1c16%200%2015%200%2015-5l3-14c5-20%202-30-7-26-3%202-10%202-13%200h-5m6%2055l-3%203-4%201c-7%200-14%205-14%2010%200%203-1%204-2%205-4%202-10%208-12%2011-1%203-1%203%201%204%202%202%202%202%200%205-2%205%201%207%209%207%204%200%205%200%207%202%202%203%205%203%2013-1l7-1c2%205%2011%206%2021%201%207-4%2011-7%2011-9l4-4c5-3%2015-13%2015-15s-1-2-5-2h-6l3-5c3-8%200-10-9-6-5%202-7%202-7-2%201-4-3-5-9-2H49c-4%201-5%201-5-1%201-3%200-3-4-1m-1%203c-2%201-2%201-1%203%201%201%201%201-1%201-1-1-2%200-3%202l-2%203v-3c1-3%201-3-1-3-6%200-12%207-11%2011%201%201%200%202-3%203-3%202-11%2010-11%2012l3%201h3l-3%204c-3%205-3%206%205%207%206%200%207%200%209%203l2%202%206-2c9-4%2011-4%2013-1%203%204%208%204%2016%200%209-3%2012-6%2012-8-1-1%200-2%203-4%206-4%2016-13%2016-15l-5-1c-7%201-7%201-3-6l2-6-12%202c-5%202-6%201-5-3%200-4-1-4-7-2-5%202-6%202-8%201h-5c-5%202-6%201-6-1%201-2%200-3-3%200\\'%20fill=\\'%23d3d3d3\\'%20fill-rule=\\'evenodd\\'/%3e%3c/svg%3e')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<picture parentName="a">{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/118c6e0ab8f4b20d6cce3ebb97e94581/ca0a1/distributed-logging.webp 223w", "/static/118c6e0ab8f4b20d6cce3ebb97e94581/75680/distributed-logging.webp 445w", "/static/118c6e0ab8f4b20d6cce3ebb97e94581/8d1ba/distributed-logging.webp 890w", "/static/118c6e0ab8f4b20d6cce3ebb97e94581/3838e/distributed-logging.webp 1335w", "/static/118c6e0ab8f4b20d6cce3ebb97e94581/9b4e7/distributed-logging.webp 1660w"],
                "sizes": "(max-width: 890px) 100vw, 890px",
                "type": "image/webp"
              }}></source>{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/118c6e0ab8f4b20d6cce3ebb97e94581/e92b6/distributed-logging.png 223w", "/static/118c6e0ab8f4b20d6cce3ebb97e94581/e66bf/distributed-logging.png 445w", "/static/118c6e0ab8f4b20d6cce3ebb97e94581/4ef49/distributed-logging.png 890w", "/static/118c6e0ab8f4b20d6cce3ebb97e94581/4e814/distributed-logging.png 1335w", "/static/118c6e0ab8f4b20d6cce3ebb97e94581/6568d/distributed-logging.png 1660w"],
                "sizes": "(max-width: 890px) 100vw, 890px",
                "type": "image/png"
              }}></source>{`
          `}<img parentName="picture" {...{
                "className": "gatsby-resp-image-image",
                "src": "/static/118c6e0ab8f4b20d6cce3ebb97e94581/4ef49/distributed-logging.png",
                "alt": "High level architecture for distributed logging and tracing",
                "title": "High level architecture for distributed logging and tracing",
                "loading": "lazy",
                "style": {
                  "width": "100%",
                  "height": "100%",
                  "margin": "0",
                  "verticalAlign": "middle",
                  "position": "absolute",
                  "top": "0",
                  "left": "0"
                }
              }}></img>{`
        `}</picture>{`
  `}</a>{`
    `}</span>{`
    `}<figcaption parentName="figure" {...{
          "className": "gatsby-resp-image-figcaption"
        }}>{`High level architecture for distributed logging and tracing`}</figcaption>{`
  `}</figure></p>
    <p>{`The system accepts batches of events, adds info about user and server state, then saves each event for easy retrieval.`}</p>
    <p>{`It was so convenient, we even used it for tracing and debugging in production. Pepper your code with `}<inlineCode parentName="p">{`console.log`}</inlineCode>{`, wait for an error, see what happened.`}</p>
    <p><video parentName="p" {...{
        "style": {
          "margin": "auto auto",
          "display": "block",
          "maxWidth": "80%"
        },
        "autoPlay": true,
        "loop": true,
        "muted": true,
        "playsInline": true,
        "loading": "lazy"
      }}>{`
            `}<source parentName="video" {...{
          "src": "https://media3.giphy.com/media/9WXyFIDv2PyBq/giphy-loop.mp4?cid=4ac046a22ygme9jjb2pcgt11mhjkzn2m09qagnz9evql7p07&rid=giphy-loop.mp4&ct=g",
          "type": "video/mp4"
        }}></source>{`
        `}</video></p>
    <p>{`A similar system can process almost anything.`}</p>
    <p>{`Great for problems you can split into independent tasks like prepping data. Less great for large inter-dependent operations like machine learning.`}</p>
    <h2 {...{
      "id": "architectures-for-serverless-data-processing"
    }}>{`Architectures for serverless data processing`}</h2>
    <p>{`Serverless data processing works like `}<inlineCode parentName="p">{`.map`}</inlineCode>{` and `}<inlineCode parentName="p">{`.reduce`}</inlineCode>{` at scale. Inspired by Google's infamous `}<a parentName="p" {...{
        "href": "https://en.wikipedia.org/wiki/MapReduce"
      }}>{`MapReduce programming model`}</a>{` and used by big data processing frameworks.`}</p>
    <p>{`Work happens in 3 steps:`}</p>
    <ol>
      <li parentName="ol">{`Accept chunks of data`}</li>
      <li parentName="ol"><strong parentName="li">{`Map`}</strong>{` over your data`}</li>
      <li parentName="ol"><strong parentName="li">{`Reduce`}</strong>{` into output format`}</li>
    </ol>
    <p>{`Say you're building an adder: multiply every number by 2 then sum.`}</p>
    <div id="lock" />
    <p>{`Using functional programming patterns in JavaScript, you'd write code like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`const result = [1, 2, 3, 4, 5] // input array
  .map(n => n * 2) // multiply each by 2
  .reduce((sum, n) => sum + n, 0) // sum together
`}</code></pre>
    <p>{`Each step is independent.`}</p>
    <p>{`The `}<inlineCode parentName="p">{`n => n*2`}</inlineCode>{` function only needs `}<inlineCode parentName="p">{`n`}</inlineCode>{`. The `}<inlineCode parentName="p">{`(sum, n) => sum+n`}</inlineCode>{` function needs the current `}<inlineCode parentName="p">{`sum`}</inlineCode>{` and `}<inlineCode parentName="p">{`n`}</inlineCode>{`.`}</p>
    <p>{`That means you can distribute the work. Run each on a separate Lambda in parallel. Thousands at a time.`}</p>
    <p>{`You go from a slow algorithm to as fast as a single operation, known as `}<a parentName="p" {...{
        "href": "https://en.wikipedia.org/wiki/Amdahl%27s_law"
      }}>{`Amdahl's Law`}</a>{`. With infinite scale, you could process an array of 10,000,000 elements almost as fast as an array of 10.`}</p>
    <p><video parentName="p" {...{
        "style": {
          "margin": "auto auto",
          "display": "block",
          "maxWidth": "80%"
        },
        "autoPlay": true,
        "loop": true,
        "muted": true,
        "playsInline": true,
        "loading": "lazy"
      }}>{`
            `}<source parentName="video" {...{
          "src": "https://media4.giphy.com/media/l4FGvUYI0tETAQwGk/giphy-loop.mp4?cid=4ac046a2ldjoslvr709otkfrg071cl3ju7frpsjosdx0nupy&rid=giphy-loop.mp4&ct=g",
          "type": "video/mp4"
        }}></source>{`
        `}</video></p>
    <p>{`Lambda limits you to 1000 parallel invocations. Individual steps can be slow (like when transcoding video) and you're limited by the `}<inlineCode parentName="p">{`reduce`}</inlineCode>{` step.`}</p>
    <p>{`Performance is best when reduce is un-necessary. Performance is worst when reduce needs to iterate over every element in 1 call and can't be distributed.`}</p>
    <p>{`Our adder is `}<a parentName="p" {...{
        "href": "https://en.wikipedia.org/wiki/Commutative_property"
      }}>{`commutative`}</a>{` and we can parallelize the reduce step using chunks of data.`}</p>
    <p><figure parentName="p" {...{
        "className": "gatsby-resp-image-figure",
        "style": {
          "textAlign": "center",
          "fontStyle": "italic"
        }
      }}>{`
    `}<span parentName="figure" {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "890px"
          }
        }}>{`
      `}<a parentName="span" {...{
            "className": "gatsby-resp-image-link",
            "href": "/static/10d65d6b860f795bf2ba48f2926b4cf0/303e6/processing-architecture.png",
            "style": {
              "display": "block"
            },
            "target": "_blank",
            "rel": "noopener"
          }}>{`
    `}<span parentName="a" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "51.12107623318386%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'204\\'%20viewBox=\\'0%200%20400%20204\\'%20preserveAspectRatio=\\'none\\'%3e%3cpath%20d=\\'M231%2094c3%201%203%201-1%201l-27%204a848%20848%200%2001-1%202c19-4%2030-5%2027-4l-1%201c0%201%208-2%208-4%201-1-1-1-4-1-4%200-4%200-1%201m153%2021c-2%201-2%203-2%209l1%207h3c5%200%207-1%208-9l2-7h-12\\'%20fill=\\'%23d3d3d3\\'%20fill-rule=\\'evenodd\\'/%3e%3c/svg%3e')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<picture parentName="a">{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/10d65d6b860f795bf2ba48f2926b4cf0/ca0a1/processing-architecture.webp 223w", "/static/10d65d6b860f795bf2ba48f2926b4cf0/75680/processing-architecture.webp 445w", "/static/10d65d6b860f795bf2ba48f2926b4cf0/8d1ba/processing-architecture.webp 890w", "/static/10d65d6b860f795bf2ba48f2926b4cf0/3838e/processing-architecture.webp 1335w", "/static/10d65d6b860f795bf2ba48f2926b4cf0/e11e5/processing-architecture.webp 1780w", "/static/10d65d6b860f795bf2ba48f2926b4cf0/26a8a/processing-architecture.webp 3362w"],
                "sizes": "(max-width: 890px) 100vw, 890px",
                "type": "image/webp"
              }}></source>{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/10d65d6b860f795bf2ba48f2926b4cf0/e92b6/processing-architecture.png 223w", "/static/10d65d6b860f795bf2ba48f2926b4cf0/e66bf/processing-architecture.png 445w", "/static/10d65d6b860f795bf2ba48f2926b4cf0/4ef49/processing-architecture.png 890w", "/static/10d65d6b860f795bf2ba48f2926b4cf0/4e814/processing-architecture.png 1335w", "/static/10d65d6b860f795bf2ba48f2926b4cf0/701e9/processing-architecture.png 1780w", "/static/10d65d6b860f795bf2ba48f2926b4cf0/303e6/processing-architecture.png 3362w"],
                "sizes": "(max-width: 890px) 100vw, 890px",
                "type": "image/png"
              }}></source>{`
          `}<img parentName="picture" {...{
                "className": "gatsby-resp-image-image",
                "src": "/static/10d65d6b860f795bf2ba48f2926b4cf0/4ef49/processing-architecture.png",
                "alt": "Distributed adder architecture",
                "title": "Distributed adder architecture",
                "loading": "lazy",
                "style": {
                  "width": "100%",
                  "height": "100%",
                  "margin": "0",
                  "verticalAlign": "middle",
                  "position": "absolute",
                  "top": "0",
                  "left": "0"
                }
              }}></img>{`
        `}</picture>{`
  `}</a>{`
    `}</span>{`
    `}<figcaption parentName="figure" {...{
          "className": "gatsby-resp-image-figcaption"
        }}>{`Distributed adder architecture`}</figcaption>{`
  `}</figure></p>
    <p>{`In practice you'll find transformations that don't need a reduce step or require many maps. All follow this basic approach. :)`}</p>
    <p>{`For the comp sci nerds 👉 this has no impact on `}<a parentName="p" {...{
        "href": "https://en.wikipedia.org/wiki/Big_O_notation"
      }}>{`big-O complexity`}</a>{`. You're changing real-world performance, not the algorithm.`}</p>
    <h2 {...{
      "id": "build-a-distributed-data-processing-pipeline"
    }}>{`Build a distributed data processing pipeline`}</h2>
    <p>{`Let's build that adder and learn how to construct a robust massively distributed data processing pipeline. We're keeping the operation simple so we can focus on the architecture.`}</p>
    <p>{`Here's the JavaScript code we're distributing:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`const result = [1, 2, 3, 4, 5] // input array
  .map((n) => n * 2) // multiply each by 2
  .reduce((sum, n) => sum + n, 0) // sum together
`}</code></pre>
    <p>{`Following principles from the `}<a parentName="p" {...{
        "href": "/serverless-architecture-principles"
      }}>{`Architecture Principles`}</a>{` chapter, we're building a system that is:`}</p>
    <ul>
      <li parentName="ul">{`easy to understand`}</li>
      <li parentName="ul">{`robust against errors`}</li>
      <li parentName="ul">{`debuggable`}</li>
      <li parentName="ul">{`replayable`}</li>
      <li parentName="ul">{`always inspectable`}</li>
    </ul>
    <p>{`We're going to use 3 `}<a parentName="p" {...{
        "href": "/serverless-elements"
      }}>{`Serverless Elements`}</a>{` to get there:`}</p>
    <ol>
      <li parentName="ol">{`lambdas`}</li>
      <li parentName="ol">{`queues`}</li>
      <li parentName="ol">{`storage`}</li>
    </ol>
    <p>{`You can see the `}<a parentName="p" {...{
        "href": "https://github.com/Swizec/serverlesshandbook.dev/tree/master/examples/serverless-data-pipeline-example"
      }}>{`full code on GitHub`}</a>{`.`}</p>
    <h3 {...{
      "id": "the-elements"
    }}>{`The elements`}</h3>
    <p>{`We're using 3 lambdas, 2 queues, and 2 DynamoDB tables.`}</p>
    <p><strong parentName="p">{`3 Lambdas`}</strong></p>
    <ol>
      <li parentName="ol"><inlineCode parentName="li">{`sumArray`}</inlineCode>{` is our API-facing lambda. Accepts a request and kicks off the process`}</li>
      <li parentName="ol"><inlineCode parentName="li">{`timesTwo`}</inlineCode>{` is the map lambda. Accepts a number, multiplies by 2, and triggers the next step.`}</li>
      <li parentName="ol"><inlineCode parentName="li">{`reduce`}</inlineCode>{` combines intermediary steps into the final result. It's the most complex.`}</li>
    </ol>
    <p>{`Our lambdas are written in TypeScript and each does 1 part of the process.`}</p>
    <p><strong parentName="p">{`2 Queues`}</strong></p>
    <ol>
      <li parentName="ol"><inlineCode parentName="li">{`TimesTwoQueue`}</inlineCode>{` holds messages from `}<inlineCode parentName="li">{`sumArray`}</inlineCode>{` and calls `}<inlineCode parentName="li">{`timesTwo`}</inlineCode>{`.`}</li>
      <li parentName="ol"><inlineCode parentName="li">{`ReduceQueue`}</inlineCode>{` holds messages from `}<inlineCode parentName="li">{`timesTwo`}</inlineCode>{` and calls `}<inlineCode parentName="li">{`reduce`}</inlineCode>{`, which also uses the queue to trigger itself.`}</li>
    </ol>
    <p>{`We're using `}<a parentName="p" {...{
        "href": "https://aws.amazon.com/sqs/"
      }}>{`SQS – Simple Queue Service – queues`}</a>{` for their reliability. An SQS queue stores messages for up to 14 days and keeps retrying until your Lambda succeeds.`}</p>
    <p>{`When something goes wrong there's little chance a message gets lost unless you're eating errors. If your code doesn't throw, SQS interprets that as success.`}</p>
    <p>{`You can configure max retries and how long a message should stick around. When it exceeds those deadlines, you can configure a Dead Letter Queue to store the message.`}</p>
    <p>{`DLQ's are useful for processing bad messages. Send an alert to yourself, store it in a different table, debug what's going on.`}</p>
    <p><strong parentName="p">{`2 tables`}</strong></p>
    <ol>
      <li parentName="ol"><inlineCode parentName="li">{`scratchpad table`}</inlineCode>{` for intermediary results from `}<inlineCode parentName="li">{`timesTwo`}</inlineCode>{`. This table makes `}<inlineCode parentName="li">{`reduce`}</inlineCode>{` easier to implement and debug.`}</li>
      <li parentName="ol"><inlineCode parentName="li">{`sums table`}</inlineCode>{` for final results`}</li>
    </ol>
    <h3 {...{
      "id": "step-1--the-api"
    }}>{`Step 1 – the API`}</h3>
    <p>{`You first need to get data into the system. We use a `}<a parentName="p" {...{
        "href": "/serverless-rest-api"
      }}>{`Serverless REST API`}</a>{`.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-yaml"
      }}>{`# serverless.yml
functions:
  sumArray:
    handler: dist/sumArray.handler
    events:
      - http:
          path: sumArray
          method: POST
          cors: true
    environment:
      timesTwoQueueURL:
        Ref: TimesTwoQueue
`}</code></pre>
    <p><inlineCode parentName="p">{`sumArray`}</inlineCode>{` is an endpoint that accepts POST requests and pipes them into the `}<inlineCode parentName="p">{`sumArray.handler`}</inlineCode>{` function. We set the URL for our `}<inlineCode parentName="p">{`TimesTwoQueue`}</inlineCode>{` as an environment variable.`}</p>
    <p>{`Define the queue like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-yaml"
      }}>{`# serverless.yml
resources:
  Resources:
    TimesTwoQueue:
      Type: "AWS::SQS::Queue"
      Properties:
        QueueName: "TimesTwoQueue-\${self:provider.stage}"
        VisibilityTimeout: 60
`}</code></pre>
    <p>{`It's an SQS queue postfixed with the current stage, which helps us split between production and development.`}</p>
    <p>{`The `}<a parentName="p" {...{
        "href": "https://docs.aws.amazon.com/AWSSimpleQueueService/latest/SQSDeveloperGuide/sqs-visibility-timeout.html"
      }}><inlineCode parentName="a">{`VisibilityTimeout`}</inlineCode></a>{` says that when your Lambda accepts a message, it has 60 seconds to process. After that, SQS assumes you never received the message and tries again.`}</p>
    <p>{`Distributed systems are fun like that.`}</p>
    <p>{`The `}<inlineCode parentName="p">{`sumArray.ts`}</inlineCode>{` Lambda that accepts requests looks like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-typescript"
      }}>{`// src/sumArray.ts

export const handler = async (event: APIGatewayEvent): Promise<APIResponse> => {
  const arrayId = uuidv4()

  if (!event.body) {
    return response(400, {
      status: "error",
      error: "Provide a JSON body",
    })
  }

  const array: number[] = JSON.parse(event.body)

  // split array into elements
  // trigger timesTwo lambda for each entry
  for (let packetValue of array) {
    await sendSQSMessage(process.env.timesTwoQueueURL!, {
      arrayId,
      packetId: uuidv4(),
      packetValue,
      arrayLength: array.length,
      packetContains: 1,
    })
  }

  return response(200, {
    status: "success",
    array,
    arrayId,
  })
}
`}</code></pre>
    <p>{`Get API request, create an `}<inlineCode parentName="p">{`arrayId`}</inlineCode>{`, parse JSON body, iterate over the input, return success and the new `}<inlineCode parentName="p">{`arrayId`}</inlineCode>{`. Consumers can later use this ID identify their result.`}</p>
    <h3 {...{
      "id": "triggering-the-next-step"
    }}>{`Triggering the next step`}</h3>
    <p>{`We turn each element of the input array into a processing packet and send it to our SQS queue as a message.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-typescript"
      }}>{`// src/sumArray.ts

for (let packetValue of array) {
  await sendSQSMessage(process.env.timesTwoQueueURL!, {
    arrayId,
    packetId: uuidv4(),
    packetValue,
    arrayLength: array.length,
    packetContains: 1,
  })
}
`}</code></pre>
    <p>{`Each packet contains:`}</p>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`arrayId`}</inlineCode>{` to identify which input it belongs to`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`packetId`}</inlineCode>{` to identify the packet itself`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`packetValue`}</inlineCode>{` as the value. You could use this to store entire JSON blobs.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`arrayLength`}</inlineCode>{` to help `}<inlineCode parentName="li">{`reduce`}</inlineCode>{` know how many packets to expect`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`packetContains`}</inlineCode>{` to help `}<inlineCode parentName="li">{`reduce`}</inlineCode>{` know when it's done`}</li>
    </ul>
    <p>{`Most properties are metadata to help our pipeline. `}<inlineCode parentName="p">{`packetValue`}</inlineCode>{` is the data we're processing.`}</p>
    <p><a parentName="p" {...{
        "href": "https://github.com/Swizec/serverlesshandbook.dev/blob/master/examples/serverless-data-pipeline-example/src/utils.ts#L11"
      }}><inlineCode parentName="a">{`sendSQSMessage`}</inlineCode></a>{` is a helper method that sends an SQS message using the AWS SDK.`}</p>
    <h3 {...{
      "id": "step-2--the-map"
    }}>{`Step 2 – the map`}</h3>
    <p>{`Our map uses the `}<inlineCode parentName="p">{`timesTwo`}</inlineCode>{` lambda and handles each packet in isolation.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-yaml"
      }}>{`# serverless.yml
functions:
  timesTwo:
    handler: dist/timesTwo.handler
    events:
      - sqs:
          arn:
            Fn::GetAtt:
              - TimesTwoQueue
              - Arn
          batchSize: 1
    environment:
      reduceQueueURL:
        Ref: ReduceQueue
`}</code></pre>
    <p><inlineCode parentName="p">{`timesTwo`}</inlineCode>{` handles events from the `}<inlineCode parentName="p">{`TimesTwoQueue`}</inlineCode>{` using the `}<inlineCode parentName="p">{`timesTwo.handler`}</inlineCode>{` method. It gets the `}<inlineCode parentName="p">{`reduceQueueURL`}</inlineCode>{` as an environment variable to trigger the next step.`}</p>
    <p>{`AWS and SQS call our lambda and keep retrying when something goes wrong. Perfect to let you re-deploy when there's a bug ✌️`}</p>
    <p>{`The lambda looks like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-typescript"
      }}>{`// src/timesTwo.ts

export const handler = async (event: SQSEvent) => {
  // grab messages from queue
  // depending on batchSize there could be multiple
  let packets: Packet[] = event.Records.map((record: SQSRecord) =>
    JSON.parse(record.body)
  )

  // iterate packets and multiply by 2
  // this would be a more expensive operation usually
  packets = packets.map((packet) => ({
    ...packet,
    packetValue: packet.packetValue * 2,
  }))

  // store each result in scratchpad table
  // in theory it's enough to put them on the queue
  // an intermediary table makes the reduce step easier to implement
  await Promise.all(
    packets.map((packet) =>
      db.updateItem({
        TableName: process.env.SCRATCHPAD_TABLE!,
        Key: { arrayId: packet.arrayId, packetId: packet.packetId },
        UpdateExpression:
          "SET packetValue = :packetValue, arrayLength = :arrayLength, packetContains = :packetContains",
        ExpressionAttributeValues: {
          ":packetValue": packet.packetValue,
          ":arrayLength": packet.arrayLength,
          ":packetContains": packet.packetContains,
        },
      })
    )
  )

  // trigger next step in calculation
  const uniqueArrayIds = Array.from(
    new Set(packets.map((packet) => packet.arrayId))
  )

  await Promise.all(
    uniqueArrayIds.map((arrayId) =>
      sendSQSMessage(process.env.reduceQueueURL!, arrayId)
    )
  )

  return true
}
`}</code></pre>
    <p>{`Accept an event from SQS, parse JSON body, do the work, store intermediary results, trigger `}<inlineCode parentName="p">{`reduce`}</inlineCode>{` step for each input.`}</p>
    <p>{`SQS might call your lambda with multiple messages depending on your `}<inlineCode parentName="p">{`batchSize`}</inlineCode>{` config. This helps you optimize cost and find the right balance between the number of executions and execution time.`}</p>
    <h3 {...{
      "id": "trigger-the-next-step"
    }}>{`Trigger the next step`}</h3>
    <p>{`Triggering the next step happens in 2 parts`}</p>
    <ol>
      <li parentName="ol">{`store intermediary result`}</li>
      <li parentName="ol">{`trigger next lambda`}</li>
    </ol>
    <p>{`Storing intermediary results makes implementing the next lambda easier. We're building a faux queue on top of DynamoDB. That's okay because it makes the system easier to debug.`}</p>
    <p>{`Something went wrong? Check intermediary table, see what's up.`}</p>
    <p>{`We iterate over results, fire a DynamoDB `}<inlineCode parentName="p">{`updateItem`}</inlineCode>{` query, and await the queries.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-typescript"
      }}>{`// src/timesTwo.ts

// store each result in scratchpad table
// in theory it's enough to put them on the queue
// an intermediary table makes the reduce step easier to implement
await Promise.all(
  packets.map((packet) =>
    db.updateItem({
      TableName: process.env.SCRATCHPAD_TABLE!,
      Key: { arrayId: packet.arrayId, packetId: packet.packetId },
      UpdateExpression:
        "SET packetValue = :packetValue, arrayLength = :arrayLength, packetContains = :packetContains",
      ExpressionAttributeValues: {
        ":packetValue": packet.packetValue,
        ":arrayLength": packet.arrayLength,
        ":packetContains": packet.packetContains,
      },
    })
  )
)
`}</code></pre>
    <p>{`Each entry in this table is uniquely identified with a combination of `}<inlineCode parentName="p">{`arrayId`}</inlineCode>{` and `}<inlineCode parentName="p">{`packetId`}</inlineCode>{`.`}</p>
    <p><em parentName="p">{`Triggering`}</em>{` the next step happens in another loop.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-typescript"
      }}>{`// src/timesTwo.ts

// trigger next step in calculation
const uniqueArrayIds = Array.from(
  new Set(packets.map((packet) => packet.arrayId))
)

await Promise.all(
  uniqueArrayIds.map((arrayId) =>
    sendSQSMessage(process.env.reduceQueueURL!, arrayId)
  )
)
`}</code></pre>
    <p>{`We use an ES6 Set to get a list of unique array ids from our input message. You never know what gets jumbled up on the queue and you might receive multiple inputs in parallel.`}</p>
    <p>{`Distributed systems are fun :)`}</p>
    <p>{`For each unique input, we trigger a reduce lambda via `}<inlineCode parentName="p">{`ReduceQueue`}</inlineCode>{`. A single `}<inlineCode parentName="p">{`reduce`}</inlineCode>{` stream makes this example easier. You should aim for parallelism in production.`}</p>
    <h3 {...{
      "id": "step-3--reduce"
    }}>{`Step 3 – reduce`}</h3>
    <p>{`Combining intermediary steps into the final result is the most complex part of our example.`}</p>
    <p>{`The simplest approach is to take the entire input and combine in 1 step. With large datasets this becomes impossible. Especially if combining elements is a big operation.`}</p>
    <p>{`You could combine 2 elements at a time and run the reduce step in parallel.`}</p>
    <p>{`Fine-tuning for number of invocations and speed of execution will result in an `}<inlineCode parentName="p">{`N`}</inlineCode>{` somewhere between `}<inlineCode parentName="p">{`2`}</inlineCode>{` and `}<inlineCode parentName="p">{`All`}</inlineCode>{`. Optimal numbers depend on what you're doing.`}</p>
    <p>{`Our reduce function uses the `}<inlineCode parentName="p">{`scratchpad table`}</inlineCode>{` as a queue:`}</p>
    <ol>
      <li parentName="ol">{`Take 2 elements`}</li>
      <li parentName="ol">{`Combine`}</li>
      <li parentName="ol">{`Write the new element`}</li>
      <li parentName="ol">{`Delete the 2 originals`}</li>
    </ol>
    <p>{`Like this:`}</p>
    <p><figure parentName="p" {...{
        "className": "gatsby-resp-image-figure",
        "style": {
          "textAlign": "center",
          "fontStyle": "italic"
        }
      }}>{`
    `}<span parentName="figure" {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "890px"
          }
        }}>{`
      `}<a parentName="span" {...{
            "className": "gatsby-resp-image-link",
            "href": "/static/f4bea68b660c07c276df45a939f7e9ac/2adcb/reduce-table.png",
            "style": {
              "display": "block"
            },
            "target": "_blank",
            "rel": "noopener"
          }}>{`
    `}<span parentName="a" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "60.0896860986547%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'240\\'%20viewBox=\\'0%200%20400%20240\\'%20preserveAspectRatio=\\'none\\'%3e%3cpath%20d=\\'M262%2025c1%205%200%206-1%204h-6l-11%201c-12%201-15%201-15%203h2l15-1c13%200%2013%200%2012%201l-4%204-5%203-11%208c-12%206-11%206-11%207l25-14c5-4%205-4%204-1l-1%203c1%201%203-1%204-5%200-4%202-6%203-6l1%2080-1%2081%203%2011%202%2018c0%206%200%206%201%203%201-4%200-16-2-22l-1-5c0-1%2014-3%2025-3l16-1c17-2%2026-3%2034-2h8v3l-1%2015c-3%2017-3%2023-2%2023l1-8%203-20c1-13%201-13%204-14%204%200%204-2%200-1h-3v-10c0-9%200-10%202-10v-1c-2-2-2-3-2-13v-11h7l-4-1h-3v-5l1-31c0-23%200-25%202-25v-1c-2-2-2-3-2-9a517%20517%200%2000-2-50h-2l2%202c1%200%202%206%200%2012v3h-37l-43%201-5%202V22c2%200%201-2-1-2l-1%205M5%2048a1849%201849%200%20005%20182c2%200%201-15-1-23l-2-8%208-1%2018-1%2021-2c11-1%2036-2%2037-1a343%20343%200%2001-4%2040%20456%20456%200%20016-38c0-1%201-2%203-2%204-1%204-3%200-2h-3v-10c0-9%200-10%202-10l-1-1c-2-1-2-2-2-12l1-11h3l3-1-3-1h-3v-10a631%20631%200%20003-51v-1c-2-2-2-4-2-31a154%20154%200%2000-1-29c0%202%200%202-1%201h-2l1%202c1%200%202%205%202%2020v20H56L8%2068l4%201c9-2%2042-2%2062-2l19%201v6l-1%208c-1%201-16%203-27%203l-23%201-25%202-11%201V75l1-22v-7l4-1%2042-1%2038-1c0-2-73-1-79%200l-5%202V24l-1-2-1%2026m128%202a5281%205281%200%20002%20158l2%2014c1%208%202%2011%202%204s0-11-2-18l-2-8%206-1%2024-2%2024-2%2018-1h12v3l-1%2011a341%20341%200%20001%206c2-21%202-20%204-20l4-1-3-1-3%201v-11c0-9%200-10%202-10h1l-1-1c-2%200-4-14-2-19%200-4%200-4%204-4%203%200%203%200%201-1l-4-1c-1%201-1%200-1-7a570%20570%200%20003-54v-1c-2%200-2-2-2-30a188%20188%200%2000-1-29c0%202%200%202-1%201h-2l2%202v15l-34-1c-30%200-41%200-49%203-2%200-2%200-2-10V23c-2-1-2%203-2%2027M98%2035l-1%201a1066%201066%200%200031%202l-9%206-24%2012h3a229%20229%200%200129-13%20748%20748%200%20001%201c4-8%202-9-8-9H98m177%207l-7%201-4%201v31l-1%2012%2011-1a685%20685%200%200048-3l18-1%2010-1V71l-1-20V41h-36l-38%201m-134%203l-5%202-1%2022-1%2021h3a719%20719%200%200176-6h8V68h-37a649%20649%200%20006-2l31%201v-4l-1-11v-8h-38l-41%201m-16%2014c-2%200-2%201-2%202l-13%208c-14%206-18%209-5%204l9-4%206-3%206-4-1%203-2%204-2%202c1%201-5%207-21%2021l-4%204%2015-12c12-10%2015-13%2016-11l1-2v-6c-1%200-1-1%201-3%202-3%200-5-4-3m147%206h-6l-1%201h7a662%20662%200%20010-1M124%2084c-3%202-5%204-3%204%203%200%200%203-13%2016l-13%2014%2013-12%2014-12c1%201-7%2014-19%2032-7%2011-8%2013-6%2013l1-1%209-14%2014-22c4-8%205-9%205-7%201%204%202%202%202-5%201-7%201-7-4-6m213%200l-16%201h-18a375%20375%200%2001-39%204v20h8a8747%208747%200%200169-4h8v-7l1-11v-4h-6l-7%201M81%2085l-20%202-53%204c-2%200-3%204-1%2015v6l7-1%2024-2c23-2%2023-2%2023-4l1-4%201%203-1%203h30V85H81m132%201l-19%201-54%204h-6v10l1%2011%2017-1c24-3%2046-4%2058-4h10V85h-3l-4%201m97%2021l-42%204h-4l1%2010%201%2010%205-1a261%20261%200%200159-5h19v-19h-14l-25%201m-257%202l-42%204H7v10c0%2011%200%2011%207%209%208-2%2031-4%2054-5h24v-19H78l-25%201m133%200l-31%203-20%201v9c0%2012%200%2012%208%2011%2010-3%2033-5%2055-5h22v-19l-12-1-22%201m132%2018c-23%201-39%202-47%205l-6%201v39h13a746%20746%200%200168-4c2%200%202-2%202-12v-11l-11%201a590%20590%200%2000-12-1l23-2%201-9v-7l-6-1-25%201m-256%202c-27%201-52%204-54%206v20l-1%2020h5l13-1a593%20593%200%200164-4c2%200%202-2%202-12v-11h-6l-20%202a394%20394%200%2000-56%204l4-1%2019-2a514%20514%200%200159-4l1-8c0-9%200-10-5-10l-25%201m115%201a361%20361%200%2001-33%204l-8%201v40h11a497%20497%200%200167-4h5v-23h-4a625%20625%200%20004-2l1-9v-8h-21l-22%201m142%2041l-55%204c-1%201%200%2019%201%2020h6l19-2%2024-2%2023-1%2011%201v-21h-3l-26%201m-258%202a427%20427%200%2000-51%204l-3%201v20l8-1%2023-2%2024-2%2018-1%2011%201v-21h-3l-27%201m156%200l-28%201c-39%201-54%203-55%206l1%2018%209-1%2018-1%2020-2%2024-1h13v-10c0-10%200-12-2-10\\'%20fill=\\'%23d3d3d3\\'%20fill-rule=\\'evenodd\\'/%3e%3c/svg%3e')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<picture parentName="a">{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/f4bea68b660c07c276df45a939f7e9ac/ca0a1/reduce-table.webp 223w", "/static/f4bea68b660c07c276df45a939f7e9ac/75680/reduce-table.webp 445w", "/static/f4bea68b660c07c276df45a939f7e9ac/8d1ba/reduce-table.webp 890w", "/static/f4bea68b660c07c276df45a939f7e9ac/3838e/reduce-table.webp 1335w", "/static/f4bea68b660c07c276df45a939f7e9ac/e11e5/reduce-table.webp 1780w", "/static/f4bea68b660c07c276df45a939f7e9ac/90d15/reduce-table.webp 1984w"],
                "sizes": "(max-width: 890px) 100vw, 890px",
                "type": "image/webp"
              }}></source>{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/f4bea68b660c07c276df45a939f7e9ac/e92b6/reduce-table.png 223w", "/static/f4bea68b660c07c276df45a939f7e9ac/e66bf/reduce-table.png 445w", "/static/f4bea68b660c07c276df45a939f7e9ac/4ef49/reduce-table.png 890w", "/static/f4bea68b660c07c276df45a939f7e9ac/4e814/reduce-table.png 1335w", "/static/f4bea68b660c07c276df45a939f7e9ac/701e9/reduce-table.png 1780w", "/static/f4bea68b660c07c276df45a939f7e9ac/2adcb/reduce-table.png 1984w"],
                "sizes": "(max-width: 890px) 100vw, 890px",
                "type": "image/png"
              }}></source>{`
          `}<img parentName="picture" {...{
                "className": "gatsby-resp-image-image",
                "src": "/static/f4bea68b660c07c276df45a939f7e9ac/4ef49/reduce-table.png",
                "alt": "How rows in the scratchpad table get combined",
                "title": "How rows in the scratchpad table get combined",
                "loading": "lazy",
                "style": {
                  "width": "100%",
                  "height": "100%",
                  "margin": "0",
                  "verticalAlign": "middle",
                  "position": "absolute",
                  "top": "0",
                  "left": "0"
                }
              }}></img>{`
        `}</picture>{`
  `}</a>{`
    `}</span>{`
    `}<figcaption parentName="figure" {...{
          "className": "gatsby-resp-image-figcaption"
        }}>{`How rows in the scratchpad table get combined`}</figcaption>{`
  `}</figure></p>
    <p>{`At each invocation we take 2 packets:`}</p>
    <pre><code parentName="pre" {...{}}>{`{
    arrayId: // ...
    packetId: // ...
    packetValue: 2,
    packetContains: 1,
    arrayLength: 10
}

{
    arrayId: // ...
    packetId: // ...
    packetValue: 4,
    packetContains: 1,
    arrayLength: 10
}
`}</code></pre>
    <p>{`And combine them into a new packet`}</p>
    <pre><code parentName="pre" {...{}}>{`// +
{
    arrayId: // ...
    packetId: // ...
    packetValue: 6
    packetContains: 2,
    arrayLength: 10
}
`}</code></pre>
    <p>{`When the count of included packets – `}<inlineCode parentName="p">{`packetContains`}</inlineCode>{` – matches the total array length – `}<inlineCode parentName="p">{`arrayLength`}</inlineCode>{` – we know this is the final result and write it into the results table.`}</p>
    <p>{`Like I said, this is the complicated part.`}</p>
    <h3 {...{
      "id": "the-reduce-step-code"
    }}>{`The reduce step code`}</h3>
    <p>{`In code, the reduce step starts like any other lambda:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-typescript"
      }}>{`// src/reduce.ts
export const handler = async (event: SQSEvent) => {
  // grab messages from queue
  // depending on batchSize there could be multiple
  let arrayIds: string[] = event.Records.map((record: SQSRecord) =>
    JSON.parse(record.body)
  )

  // process each ID from batch
  await Promise.all(arrayIds.map(reduceArray))
}
`}</code></pre>
    <p>{`Grab `}<inlineCode parentName="p">{`arrayId`}</inlineCode>{`s from the SQS event and wait until every `}<inlineCode parentName="p">{`reduceArray`}</inlineCode>{` call is done.`}</p>
    <p>{`Then the `}<inlineCode parentName="p">{`reduceArray`}</inlineCode>{` function does its job:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-typescript"
      }}>{`// src/reduce.ts

async function reduceArray(arrayId: string) {
  // grab 2 entries from scratchpad table
  // IRL you'd grab as many as you can cost-effectively process in execution
  // depends what you're doing
  const packets = await readPackets(arrayId)

  if (packets.length > 0) {
    // sum packets together
    const sum = packets.reduce(
      (sum: number, packet: Packet) => sum + packet.packetValue,
      0
    )

    // add the new item sum to scratchpad table
    // we do this first so we don't delete rows if it fails
    const newPacket = {
      arrayId,
      packetId: uuidv4(),
      arrayLength: packets[0].arrayLength,
      packetValue: sum,
      packetContains: packets.reduce(
        (count: number, packet: Packet) => count + packet.packetContains,
        0
      ),
    }
    await db.updateItem({
      TableName: process.env.SCRATCHPAD_TABLE!,
      Key: {
        arrayId,
        packetId: uuidv4(),
      },
      UpdateExpression:
        "SET packetValue = :packetValue, arrayLength = :arrayLength, packetContains = :packetContains",
      ExpressionAttributeValues: {
        ":packetValue": newPacket.packetValue,
        ":arrayLength": newPacket.arrayLength,
        ":packetContains": newPacket.packetContains,
      },
    })

    // delete the 2 rows we just summed
    await cleanup(packets)

    // are we done?
    if (newPacket.packetContains >= newPacket.arrayLength) {
      // done, save sum to final table
      await db.updateItem({
        TableName: process.env.SUMS_TABLE!,
        Key: {
          arrayId,
        },
        UpdateExpression: "SET resultSum = :resultSum",
        ExpressionAttributeValues: {
          ":resultSum": sum,
        },
      })
    } else {
      // not done, trigger another reduce step
      await sendSQSMessage(process.env.reduceQueueURL!, arrayId)
    }
  }
}
`}</code></pre>
    <p>{`Gets 2 entries from the DynamoDB table`}<sup parentName="p" {...{
        "id": "fnref-2"
      }}><a parentName="sup" {...{
          "href": "#fn-2",
          "className": "footnote-ref"
        }}>{`2`}</a></sup>{` and combines them into a new packet.`}</p>
    <p>{`This packet `}<em parentName="p">{`must`}</em>{` have a new `}<inlineCode parentName="p">{`packetId`}</inlineCode>{`. It's a new entry for the faux processing queue on DynamoDB.`}</p>
    <p>{`Insert that back. If it succeeds, remove the previous 2 elements using their `}<inlineCode parentName="p">{`arrayId/packetId`}</inlineCode>{` combination.`}</p>
    <p>{`If our new `}<inlineCode parentName="p">{`packetContains`}</inlineCode>{` is greater than or equal to the total `}<inlineCode parentName="p">{`arrayLength`}</inlineCode>{`, we know the computation is complete. Write results to the final table.`}</p>
    <h2 {...{
      "id": "conclusion"
    }}>{`Conclusion`}</h2>
    <p>{`Lambda processing pipelines are a powerful tool that can process large amounts of data in near real-time. I have yet to find a way to swamp one of these in production.`}</p>
    <p>{`Slow downs happen where you have to introduce throttling to limit processing because a downstream system can't take the load.`}</p>
    <p>{`✌️`}</p>
    <p>{`Next chapter, we talk about monitoring serverless apps.`}</p>

    <div {...{
      "className": "footnotes"
    }}>
      <hr parentName="div"></hr>
      <ol parentName="div">
        <li parentName="ol" {...{
          "id": "fn-1"
        }}>{`Our dataloss happened because of Postgres. Sometimes it would fail to insert a row, but wouldn't throw an error. We mitigated with various workarounds but a small loss remained. At that point we decided it was good enough.`}<a parentName="li" {...{
            "href": "#fnref-1",
            "className": "footnote-backref"
          }}>{`↩`}</a></li>
        <li parentName="ol" {...{
          "id": "fn-2"
        }}>{`We use a `}<inlineCode parentName="li">{`Limit: 2`}</inlineCode>{` argument to limit how much of the table we scan through. Don't need more than 2, don't read more than 2. Keeps everything snappy`}<a parentName="li" {...{
            "href": "#fnref-2",
            "className": "footnote-backref"
          }}>{`↩`}</a></li>
      </ol>
    </div>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      